import { useNavigate } from "react-router-dom";
import {
  Activity,
  Building2,
  CircleCheck,
  CreditCard,
  Loader2,
  Users,
} from "lucide-react";
import { useAdminStats, useHealth } from "@/lib/api/stats.queries";
import { useAdminDianProcesses } from "@/lib/api/dian-processes.queries";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { DianStatusBadge } from "@/components/ui/Badge";
import { formatDate } from "@/lib/utils";

export function Dashboard() {
  const navigate = useNavigate();
  const health = useHealth();
  const stats = useAdminStats();
  const recentProcesses = useAdminDianProcesses({ pageSize: 10, page: 1 });

  const s = stats.data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">
          Herramientas administrativas para operadores de PahFacturar.
        </p>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Salud API
            </p>
            <div className="mt-3 flex items-center gap-2">
              {health.isPending ? (
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              ) : health.isSuccess ? (
                <CircleCheck className="h-5 w-5 text-emerald-500" />
              ) : (
                <span className="h-2 w-2 rounded-full bg-red-500" />
              )}
              <span className="text-sm font-medium text-slate-900">
                {health.isPending ? "Verificando" : health.isSuccess ? "Operacional" : "Caída"}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Equipos
            </p>
            <div className="mt-3 flex items-center gap-2">
              <Users className="h-5 w-5 text-brand-500" />
              <span className="text-2xl font-semibold text-slate-900">
                {stats.isPending ? "…" : (s?.teamsCount ?? "—")}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Empresas
            </p>
            <div className="mt-3 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-brand-500" />
              <span className="text-2xl font-semibold text-slate-900">
                {stats.isPending ? "…" : (s?.companiesCount ?? "—")}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Suscripciones activas
            </p>
            <div className="mt-3 flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-brand-500" />
              <span className="text-2xl font-semibold text-slate-900">
                {stats.isPending ? "…" : (s?.activeSubscriptions ?? "—")}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* DIAN 24h stats */}
      {s?.dianProcessesLast24h && (
        <Card>
          <CardHeader>
            <CardTitle>Procesos DIAN — últimas 24 h</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {(
                [
                  { label: "En cola", key: "queued", color: "bg-amber-400" },
                  { label: "Procesando", key: "processing", color: "bg-sky-400" },
                  { label: "Completados", key: "completed", color: "bg-emerald-500" },
                  { label: "Fallidos", key: "failed", color: "bg-red-500" },
                ] as const
              ).map(({ label, key, color }) => (
                <div key={key} className="flex items-center gap-3">
                  <div className={`h-3 w-3 rounded-full ${color}`} />
                  <div>
                    <p className="text-xs text-slate-500">{label}</p>
                    <p className="text-lg font-semibold text-slate-900">
                      {s.dianProcessesLast24h[key]}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick actions + Recent processes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-brand-600" />
              <p className="text-sm font-medium text-slate-900">Acciones rápidas</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" onClick={() => navigate("/teams")}>
                Ver equipos
              </Button>
              <Button size="sm" variant="outline" onClick={() => navigate("/companies")}>
                Ver empresas
              </Button>
              <Button size="sm" variant="outline" onClick={() => navigate("/subscriptions")}>
                Suscripciones
              </Button>
              <Button size="sm" variant="outline" onClick={() => navigate("/dian-processes")}>
                Procesos DIAN
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Procesos recientes</CardTitle>
              <Button size="sm" variant="ghost" onClick={() => navigate("/dian-processes")}>
                <Activity className="h-3.5 w-3.5" /> Ver todos
              </Button>
            </div>
          </CardHeader>
          {recentProcesses.isPending ? (
            <CardContent className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
              <span className="text-sm text-slate-500">Cargando…</span>
            </CardContent>
          ) : (recentProcesses.data?.processes ?? []).length === 0 ? (
            <CardContent>
              <p className="text-sm text-slate-500">No hay procesos recientes.</p>
            </CardContent>
          ) : (
            <div className="divide-y divide-slate-100">
              {(recentProcesses.data?.processes ?? []).slice(0, 8).map((p) => (
                <div key={p.processId} className="px-5 py-2.5 flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-mono text-slate-700 truncate">{p.companyNit}</p>
                    <p className="text-[11px] text-slate-500">
                      {p.documentType} · {formatDate(p.createdAt)}
                    </p>
                  </div>
                  <DianStatusBadge status={p.status} />
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
